import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

const SubmenuPortal = ({ openSubmenuId, items, submenuLayout, menuRef, onNavigate }) => {
  const openItem = items.find((i) => i.id === openSubmenuId && i.submenu?.length);
  if (!openItem || !submenuLayout) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[11rem] rounded-lg border border-theme-secondary bg-theme-main py-1 shadow-lg"
      style={{
        top: submenuLayout.top,
        left: submenuLayout.left,
        minWidth: Math.max(submenuLayout.minWidth, 176),
      }}
      role="menu"
    >
      {openItem.submenu.map((sub) => (
        <Link
          key={sub.id}
          to={sub.to}
          role="menuitem"
          onClick={onNavigate}
          className={`block px-3 py-2 text-sm transition-colors ${
            sub.active
              ? "bg-theme-secondary text-theme-primary font-medium"
              : "text-theme-text hover:bg-theme-secondary"
          }`}
        >
          {sub.label}
        </Link>
      ))}
    </div>,
    document.body
  );
};

const TabNavigation = ({ items, compact = false }) => {
  const [openSubmenuId, setOpenSubmenuId] = useState(null);
  const [submenuLayout, setSubmenuLayout] = useState(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRefs = useRef({});

  const measureSubmenu = useCallback((id) => {
    if (!id) {
      setSubmenuLayout(null);
      return;
    }
    const el = triggerRefs.current[id];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const minW = Math.max(r.width, 176);
    const padding = 8;
    const left = Math.min(r.left, Math.max(padding, window.innerWidth - minW - padding));
    setSubmenuLayout({
      top: r.bottom + 4,
      left,
      minWidth: minW,
    });
  }, []);

  useLayoutEffect(() => {
    measureSubmenu(openSubmenuId);
  }, [openSubmenuId, measureSubmenu]);

  useEffect(() => {
    if (!openSubmenuId) return undefined;
    const onReposition = () => measureSubmenu(openSubmenuId);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [openSubmenuId, measureSubmenu]);

  useEffect(() => {
    if (!openSubmenuId) return undefined;
    const onDocMouseDown = (e) => {
      const trigger = triggerRefs.current[openSubmenuId];
      const menu = menuRef.current;
      const t = e.target;
      if (trigger?.contains(t) || menu?.contains(t)) return;
      setOpenSubmenuId(null);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [openSubmenuId]);

  const closeSubmenu = useCallback(() => {
    setOpenSubmenuId(null);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full min-w-0 max-w-full">
      <nav
        className="flex w-full min-w-0 max-w-full space-x-4 sm:space-x-8 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Tabs"
      >
        {items.map((item) => {
          const className = `
            ${compact ? "py-1 px-1" : "py-3 sm:py-4 px-1 sm:px-1.5"} border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 whitespace-nowrap shrink-0
              ${
                item.active
                  ? "border-theme-primary text-theme-primary"
                  : "border-transparent text-theme-text hover:text-theme-primary hover:border-theme-secondary"
              }
          `;

          const content = (
            <>
              {item.icon && <span className="text-base">{item.icon}</span>}
              <span className={item.mobileIconOnly ? "hidden sm:inline" : ""}>{item.label}</span>
              {item.count !== undefined && (
                <span
                  className={`ml-1 bg-theme-secondary text-theme-text opacity-80 px-2 py-0.5 rounded-full text-xs ${
                    item.mobileIconOnly ? "hidden sm:inline-flex" : "inline-flex"
                  }`}
                >
                  {item.count}
                </span>
              )}
              {item.submenu && item.submenu.length > 0 && (
                <svg className="w-3.5 h-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </>
          );

          if (item.submenu && item.submenu.length > 0) {
            const isOpen = openSubmenuId === item.id;
            return (
              <div key={item.id} className="relative shrink-0">
                <button
                  ref={(el) => {
                    triggerRefs.current[item.id] = el;
                  }}
                  type="button"
                  onClick={() => {
                    setOpenSubmenuId(isOpen ? null : item.id);
                  }}
                  className={className}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                >
                  {content}
                </button>
              </div>
            );
          }

          if (item.to) {
            return (
              <Link
                key={item.id}
                to={item.to}
                className={className}
                aria-label={item.mobileIconOnly ? item.label : undefined}
              >
                {content}
              </Link>
            );
          }

          return (
            <button
              key={item.id}
              type="button"
              onClick={item.onClick}
              className={className}
              aria-label={item.mobileIconOnly ? item.label : undefined}
            >
              {content}
            </button>
          );
        })}
      </nav>

      <SubmenuPortal
        openSubmenuId={openSubmenuId}
        items={items}
        submenuLayout={submenuLayout}
        menuRef={menuRef}
        onNavigate={closeSubmenu}
      />
    </div>
  );
};

export default TabNavigation;
